<template>
  <ion-page>
    <ion-header :translucent="true">
      <ion-toolbar>
        <ion-buttons slot="end">
          <ion-button @click="dismiss">
            <ion-icon slot="icon-only" :icon="icons.close"></ion-icon>
          </ion-button>
        </ion-buttons>
        <ion-title>
          {{ $t('config.title') }}
        </ion-title>
      </ion-toolbar>
    </ion-header>

    <ion-content>
      <ion-list lines="full">
        <ion-list-header>
          <ion-label>{{ $t('config.photoSection') }}</ion-label>
        </ion-list-header>
        <ion-item
          :disabled="photoForcedPixelateFaces"
          :lines="photoForcedPixelateFaces ? 'none' : 'full'"
        >
          <ion-label class="ion-text-wrap">
            {{ $t('photoSettings.pixelateFaces') }}
            <p>{{ $t('photoSettings.pixelateFacesHelp') }}</p>
          </ion-label>
          <ion-toggle slot="end" v-model="pixelateFaces"></ion-toggle>
        </ion-item>
        <!-- Kept outside the disabled item so the explanation stays readable. -->
        <ion-item v-if="photoForcedPixelateFaces">
          <ion-icon
            slot="start"
            color="medium"
            :icon="icons.lockClosed"
          ></ion-icon>
          <ion-label color="medium" class="ion-text-wrap">
            <p>{{ $t('photoSettings.forcedByProject') }}</p>
          </ion-label>
        </ion-item>

        <ion-item
          :disabled="photoForcedTimestamp"
          :lines="photoForcedTimestamp ? 'none' : 'full'"
        >
          <ion-label class="ion-text-wrap">
            {{ $t('photoSettings.timestamp') }}
            <p>{{ $t('photoSettings.timestampHelp') }}</p>
          </ion-label>
          <ion-toggle slot="end" v-model="timestamp"></ion-toggle>
        </ion-item>
        <ion-item v-if="photoForcedTimestamp">
          <ion-icon
            slot="start"
            color="medium"
            :icon="icons.lockClosed"
          ></ion-icon>
          <ion-label color="medium" class="ion-text-wrap">
            <p>{{ $t('photoSettings.forcedByProject') }}</p>
          </ion-label>
        </ion-item>

        <ion-item :disabled="!pixelateFaces" lines="none">
          <ion-label class="ion-text-wrap">
            {{ $t('photoSettings.detectionStrength') }}
            <p>{{ $t('photoSettings.detectionStrengthHelp') }}</p>
          </ion-label>
        </ion-item>
        <ion-item :disabled="!pixelateFaces">
          <ion-segment v-model="detectionStrength" mode="ios">
            <ion-segment-button value="standard">
              <ion-label>{{ $t('photoSettings.strengths.standard') }}</ion-label>
            </ion-segment-button>
            <ion-segment-button value="high">
              <ion-label>{{ $t('photoSettings.strengths.high') }}</ion-label>
            </ion-segment-button>
            <ion-segment-button value="max">
              <ion-label>{{ $t('photoSettings.strengths.max') }}</ion-label>
            </ion-segment-button>
          </ion-segment>
        </ion-item>
      </ion-list>
    </ion-content>
  </ion-page>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';

import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButton,
  IonButtons,
  IonIcon,
  IonList,
  IonListHeader,
  IonItem,
  IonLabel,
  IonToggle,
  IonSegment,
  IonSegmentButton,
  modalController,
} from '@ionic/vue';
import { close, lockClosed } from 'ionicons/icons';

export default {
  name: 'ODNModalConfig',
  components: {
    IonContent,
    IonHeader,
    IonPage,
    IonTitle,
    IonToolbar,
    IonButton,
    IonButtons,
    IonIcon,
    IonList,
    IonListHeader,
    IonItem,
    IonLabel,
    IonToggle,
    IonSegment,
    IonSegmentButton,
  },
  data() {
    return {
      icons: {
        close,
        lockClosed,
      },
    };
  },
  computed: {
    ...mapState('prefs', ['photoDetectionStrength']),
    ...mapGetters('prefs', [
      'photoForcedPixelateFaces',
      'photoForcedTimestamp',
      'photoPixelateFacesEffective',
      'photoTimestampEffective',
    ]),
    // Each control reads from the store and writes straight back through its
    // persist action, so every change is saved immediately. The modal can then
    // be closed at any time without losing anything.
    //
    // A setting forced by the current project is shown as enabled but read-only:
    // the write is skipped so the user preference underneath is left untouched
    // and comes back as soon as the forcing context is left.
    pixelateFaces: {
      get() {
        return this.photoPixelateFacesEffective;
      },
      set(value) {
        if (this.photoForcedPixelateFaces) return;

        this.setPhotoPixelateFaces(value);
      },
    },
    timestamp: {
      get() {
        return this.photoTimestampEffective;
      },
      set(value) {
        if (this.photoForcedTimestamp) return;

        this.setPhotoTimestamp(value);
      },
    },
    detectionStrength: {
      get() {
        return this.photoDetectionStrength;
      },
      set(value) {
        this.setPhotoDetectionStrength(value);
      },
    },
  },
  methods: {
    ...mapActions('prefs', [
      'setPhotoPixelateFaces',
      'setPhotoTimestamp',
      'setPhotoDetectionStrength',
    ]),
    dismiss() {
      modalController.dismiss();
    },
  },
};
</script>
